/**
 * IMPORT CSV - ÉTAPE 3 : PREVIEW
 * Aperçu des données avec validation
 */

import { useMemo } from 'react';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Badge } from '../ui';
import type { CSVColumnMapping, CSVImportConfig } from '../../types/csv';
import { validateCSVRow } from '../../lib/csv-import';

interface ImportStepPreviewProps<T> {
  csvData: { rows: Record<string, string>[] };
  mapping: CSVColumnMapping[];
  config: CSVImportConfig<T>;
}

export function ImportStepPreview<T>({ csvData, mapping, config }: ImportStepPreviewProps<T>) {
  // Transformer et valider les 5 premières lignes
  const previewData = useMemo(() => {
    return csvData.rows.slice(0, 5).map((csvRow, index) => {
      const lineNumber = index + 2; // +2 car header + index base 0

      // Créer un objet transformé basé sur le mapping
      const mappedRow: Record<string, string> = {};
      mapping.forEach((map) => {
        if (map.dbField && map.dbField !== 'ignore') {
          mappedRow[map.dbField] = csvRow[map.csvColumn] || '';
        }
      });

      // Valider la ligne
      const errors = validateCSVRow(mappedRow, mapping, config, lineNumber);

      return {
        lineNumber,
        csvRow,
        mappedRow,
        errors,
        isValid: errors.length === 0,
      };
    });
  }, [csvData.rows, mapping, config]);

  const validCount = previewData.filter((d) => d.isValid).length;
  const errorCount = previewData.length - validCount;

  // Estimer le total d'erreurs sur toutes les lignes
  const estimatedErrors = Math.round((errorCount / previewData.length) * csvData.rows.length);

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-semibold mb-3">Aperçu et validation</h3>
        <p className="text-base text-gray-600">
          Vérifiez les {previewData.length} premières lignes avant l'import
        </p>
        <div className="mt-4 flex items-center justify-center gap-4">
          <Badge variant={validCount > 0 ? 'default' : 'secondary'}>
            <CheckCircle className="mr-1 h-3 w-3" />
            {validCount} valides
          </Badge>
          <Badge variant={errorCount > 0 ? 'destructive' : 'secondary'}>
            <XCircle className="mr-1 h-3 w-3" />
            {errorCount} erreurs
          </Badge>
        </div>
      </div>

      {/* Table de preview */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-base">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700 w-20">Ligne</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700 w-20">Status</th>
                {mapping
                  .filter((m) => m.dbField && m.dbField !== 'ignore')
                  .map((map) => (
                    <th
                      key={map.dbField}
                      className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap"
                    >
                      {config.fieldLabels[map.dbField as keyof T] || map.dbField}
                      {map.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {previewData.map((row) => (
                <tr key={row.lineNumber} className={row.isValid ? '' : 'bg-red-50'}>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.lineNumber}</td>
                  <td className="px-4 py-3">
                    {row.isValid ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <div className="relative group">
                        <XCircle className="h-5 w-5 text-red-600 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-72 p-3 bg-gray-900 text-white text-sm rounded shadow-lg">
                          {row.errors.map((err, i) => (
                            <div key={i} className="mb-1 last:mb-0">
                              <span className="font-medium">{err.field}:</span> {err.error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </td>
                  {mapping
                    .filter((m) => m.dbField && m.dbField !== 'ignore')
                    .map((map) => {
                      const value = row.mappedRow[map.dbField!];
                      const hasError = row.errors.some((e) => e.field === map.dbField);

                      return (
                        <td
                          key={map.dbField}
                          className={`px-4 py-3 ${hasError ? 'text-red-700 font-medium' : 'text-gray-900'}`}
                        >
                          <div className="max-w-xs truncate" title={value || '(vide)'}>
                            {value || <span className="text-gray-400 italic">(vide)</span>}
                          </div>
                        </td>
                      );
                    })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Résumé des erreurs */}
      {errorCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <AlertCircle className="h-6 w-6 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-base text-yellow-800 space-y-2">
              <p className="font-medium text-lg">
                {errorCount} erreur{errorCount > 1 ? 's' : ''} détectée{errorCount > 1 ? 's' : ''}{' '}
                dans l'aperçu
              </p>
              <p>
                Sur la base de cet échantillon, environ <strong>{estimatedErrors}</strong> lignes
                pourraient contenir des erreurs sur les {csvData.rows.length} lignes totales.
              </p>
              <p className="text-sm">
                Les lignes avec erreurs seront ignorées lors de l'import. Vous pourrez exporter un
                rapport d'erreurs à la fin.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Informations sur l'import */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <AlertCircle className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-base text-blue-800 space-y-2">
            <p className="font-medium text-lg">Informations :</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>{csvData.rows.length} lignes seront traitées</li>
              <li>Import par batch de 50 lignes</li>
              <li>Les lignes invalides seront automatiquement ignorées</li>
              <li>Un rapport détaillé sera généré à la fin</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
